import { flip, offset, shift } from '@floating-ui/react-dom';
import { ButtonElement } from '@typedream/core';
import { setNodes, toDOMNode } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import { Floating } from '../../../components/Floating';
import { SelectMedia } from '../../../components/SelectMedia';
import { OverlayPortal } from '../../../overlay/OverlayPortal';
import { useTPlateEditorRef } from '../../../plate';
import {
  addButtonIcon,
  removeButtonIcon,
} from '../../../nodes/button/transforms';
import {
  getSelectedElementEntry,
  useSelectedElementEntry,
} from '../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../plugins/store';
import toolbarStyles from '../SideToolbar.module.css';

function ButtonIcon() {
  const editor = useTPlateEditorRef();
  const selectedElementEntry = useSelectedElementEntry<ButtonElement>();
  const [isReplacingIcon, setIsReplacingIcon] = useState(false);

  const hasIcon = useDreamEditorStore(() => {
    const entry = getSelectedElementEntry<ButtonElement>(editor);
    return !!entry?.[0].icon;
  });

  useEffect(() => {
    const handler = () => setIsReplacingIcon((v) => !v);
    editor.emitter.on('toolbar:toggle-replace-button-icon', handler);
    return () => editor.emitter.off('toolbar:toggle-replace-button-icon', handler);
  }, [editor.emitter]);

  if (!selectedElementEntry) return null;
  const [element, path] = selectedElementEntry;
  const domNode = toDOMNode(editor, element);

  const handleClick = () => {
    if (hasIcon) {
      removeButtonIcon(editor, path);
      setIsReplacingIcon(false);
    } else {
      addButtonIcon(editor, path);
    }
  };

  return (
    <div className={toolbarStyles.section}>
      <span className={toolbarStyles.label}>Button Icon</span>
      <button
        type="button"
        className={classNames(toolbarStyles.button)}
        onClick={handleClick}
      >
        <span>
          {hasIcon ? 'Remove' : 'Add'}
          {' '}
          Icon
        </span>
      </button>

      {hasIcon && isReplacingIcon && domNode && (
        <OverlayPortal>
          <Floating
            reference={domNode}
            options={{
              placement: 'bottom',
              middleware: [
                offset(-6),
                flip(),
                shift({
                  crossAxis: true,
                  padding: { top: 8, bottom: 8 },
                }),
              ],
            }}
            autoUpdate
            className={toolbarStyles.box}
          >
            <SelectMedia
              tabs={{

                icon: {
                  onSelect: (icon) => {
                    setNodes(
                      editor,
                      {
                        icon: {
                          type: 'icon',
                          content: icon,
                        },
                      },
                      { at: path },
                    );
                    setIsReplacingIcon(false);
                  },
                },
                emoji: {
                  onSelect: (emoji) => {
                    setNodes(
                      editor,
                      {
                        icon: {
                          type: 'emoji',
                          content: emoji,
                        },
                      },
                      { at: path },
                    );
                    setIsReplacingIcon(false);
                  },
                },
                upload: {
                  accept: 'image/*',
                  button: 'Choose an image',
                  onSelect: async (file) => {
                    if (!editor.fileManager) return;

                    try {
                      const url = await editor.fileManager.upload(file);

                      setNodes(
                        editor,
                        {
                          icon: {
                            type: 'image',
                            content: url,
                          },
                        },
                        { at: path },
                      );
                      setIsReplacingIcon(false);
                    } catch (e) {
                      // do nothing
                    }
                  },
                },
                link: {
                  button: 'Use this image',
                  placeholder: 'https://some-image-url.com/your-image',
                  defaultValue:
                    element.icon?.type === 'image'
                      ? element.icon.content
                      : undefined,
                  onSelect: (url) => {
                    setNodes(
                      editor,
                      {
                        icon: {
                          type: 'image',
                          content: url,
                        },
                      },
                      { at: path },
                    );
                    setIsReplacingIcon(false);
                  },
                },
              }}
            />
          </Floating>
        </OverlayPortal>
      )}
    </div>
  );
}

export default memo(ButtonIcon);
